import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useAnalyticsConsentUi } from '@/context/AnalyticsConsentUiContext';
import {
  getGaMeasurementId,
  getStoredAnalyticsConsent,
  userDeniedAnalyticsConsent,
  userGrantedAnalyticsConsent,
} from '@utils/analytics';

const PRIVACY_URL = 'https://www.svenesis.org/privacy/privacy_en.html';

/**
 * Consent UI when GA is configured. gtag loads at startup with Consent Mode v2 default denied;
 * Accept/Decline updates consent; no analytics cookies until granted.
 */
const AnalyticsConsentBanner = () => {
  const measurementId = getGaMeasurementId();
  const { settingsNonce } = useAnalyticsConsentUi();
  const [showBar, setShowBar] = useState(false);
  const lastProcessedNonceRef = useRef(0);

  useEffect(() => {
    if (!measurementId) {
      return;
    }

    if (settingsNonce > lastProcessedNonceRef.current) {
      lastProcessedNonceRef.current = settingsNonce;
      setShowBar(true);
      return;
    }

    if (settingsNonce !== 0) {
      return;
    }

    const existing = getStoredAnalyticsConsent();
    if (existing === 'granted') {
      setShowBar(false);
    } else if (existing === 'denied') {
      setShowBar(false);
    } else {
      setShowBar(true);
    }
  }, [measurementId, settingsNonce]);

  const onAccept = useCallback(() => {
    if (!measurementId) {
      return;
    }
    userGrantedAnalyticsConsent();
    setShowBar(false);
  }, [measurementId]);

  const onDecline = useCallback(() => {
    userDeniedAnalyticsConsent();
    setShowBar(false);
  }, []);

  if (!measurementId || !showBar) {
    return null;
  }

  return (
    <div className="analytics-consent-banner" role="region" aria-label="Analytics consent">
      <Alert
        type="info"
        header="Analytics"
        action={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="primary" onClick={onAccept}>
              Accept analytics
            </Button>
            <Button variant="link" onClick={onDecline}>
              Decline
            </Button>
          </SpaceBetween>
        }
      >
        <Box variant="p" margin={{ bottom: 's' }}>
          We use Google Analytics 4 to understand how this tool is used (aggregated, no sale of personal data).
          Data is processed according to our{' '}
          <Link href={PRIVACY_URL} external>
            Privacy Policy
          </Link>
          . You can reopen <strong>Cookie-Settings</strong> in the page header to change your choice.
        </Box>
      </Alert>
    </div>
  );
};

export default AnalyticsConsentBanner;
